/**
 * src/lib/download.ts — authenticated file download for server-generated exports (web).
 *
 * The reporting CSVs that BEAM builds server-side (CPA year-end, income/expense year-end, Gusto
 * labor) are owner/office-gated, so the browser must send the session's Bearer token (and, on web
 * sessions, the auth cookie). This fetches the file with auth, reads the server filename from
 * Content-Disposition, and saves it via a Blob + anchor. No-op on native (downloadSupported()
 * is false; callers hide the button there and show the data on-screen instead).
 *
 * Complements src/lib/csv.ts, which builds CSVs purely client-side from data already loaded.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import { Platform } from 'react-native';
import { API_BASE_URL } from '../config';
import { useSession } from '../auth/session';

export function downloadSupported(): boolean {
  return Platform.OS === 'web' && typeof document !== 'undefined' && typeof Blob !== 'undefined';
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return m?.[1] ? decodeURIComponent(m[1]) : fallback;
}

/**
 * GET `path` with the session token and save the response as a file.
 * `path` is an API path (e.g. "/api/reports/year-end/2025.csv"). Throws Error on failure.
 */
export async function downloadAuthedFile(path: string, fallbackFilename: string): Promise<void> {
  if (!downloadSupported()) return;
  const token = useSession.getState().token;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include', // web sessions also carry an httpOnly auth cookie
  });
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error?.message) message = String(body.error.message);
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message);
  }
  const filename = filenameFromDisposition(res.headers.get('Content-Disposition'), fallbackFilename);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
